import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { IndianRupee, Search, Check, X, History, Plus, Undo2, FileDown, ChevronDown, Upload } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

// --- Helpers -------------------------------------------------------------
const STATUS_BADGE = {
  approved:  { label: "Approved",           tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  pending:   { label: "Pending Approval",   tone: "bg-amber-100 text-amber-900 border-amber-200" },
  rejected:  { label: "Un-Approved",        tone: "bg-rose-100 text-rose-800 border-rose-200" },
  no_price:  { label: "No price yet",       tone: "bg-slate-100 text-slate-600 border-slate-200" },
};

const fmtMoney = (currency, price) => {
  if (price === null || price === undefined || price === "") return "—";
  const n = Number(price);
  if (!Number.isFinite(n)) return "—";
  return `${currency || ""} ${n.toFixed(2)}`.trim();
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y) return iso;
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${d} ${MONTHS[Number(m) - 1] || m} ${y}`;
};

// --- Component -----------------------------------------------------------
export default function PriceList() {
  const { user } = useAuth();
  const caps = useMemo(() => new Set(user?.capabilities || []), [user]);
  const isAdmin = user?.role === "admin" || user?.role === "admin_addl";
  const canSubmit = isAdmin || caps.has("submit_price");
  const canApprove = isAdmin || caps.has("approve_price");
  const canExport = isAdmin || caps.has("export_price_list");
  const canImport = isAdmin || caps.has("historical_import_prices");
  const canSeePrices = isAdmin || caps.has("view_price_values");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buyer, setBuyer] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [currencies, setCurrencies] = useState([]);

  // Submit / update dialog state
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitRow, setSubmitRow] = useState(null);
  const [form, setForm] = useState({ currency: "USD", price: "", price_terms: "", effective_date: "" });

  // Reject dialog state
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectEntry, setRejectEntry] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // History drill-down
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyStyle, setHistoryStyle] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);

  // Bulk-approve selection (pending rows only)
  const [selectedPendingIds, setSelectedPendingIds] = useState(new Set());

  // Historical import
  const [priceTerms, setPriceTerms] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (buyer.trim()) params.buyer = buyer.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      if (q.trim()) params.q = q.trim();
      const res = await api.get("/price-list", { params });
      setRows(res.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load price list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [buyer, statusFilter]);
  useEffect(() => {
    api.get("/price-list/currencies").then((r) => {
      setCurrencies(r.data.currencies || []);
      setPriceTerms(r.data.price_terms || []);
    }).catch(() => {});
  }, []);
  // Clear the multi-select whenever the visible row set changes so a hidden
  // ticked row can't sneak into a bulk approve.
  useEffect(() => { setSelectedPendingIds(new Set()); }, [buyer, statusFilter, q, rows.length]);

  const bulkApprove = async () => {
    const ids = Array.from(selectedPendingIds);
    if (!ids.length) return;
    if (!window.confirm(`Approve ${ids.length} pending price submission(s)?`)) return;
    try {
      const res = await api.post("/price-list/bulk-approve", { entry_ids: ids });
      const skipCount = (res.data.skipped || []).length;
      toast.success(`Approved ${res.data.approved} price(s)${skipCount ? ` · ${skipCount} skipped` : ""}`);
      setSelectedPendingIds(new Set());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk approve failed"); }
  };

  const downloadImportTemplate = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/price-list/import-template.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "price_list_import_template.xlsx";
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) { toast.error(`Template download failed: ${e.message || e}`); }
  };

  const doHistoricalImport = async () => {
    if (!importFile) { toast.error("Pick an Excel file first"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const res = await api.post("/price-list/historical-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.imported} row(s)${res.data.superseded ? ` · superseded ${res.data.superseded} prior approved` : ""}${res.data.errors?.length ? ` · ${res.data.errors.length} skipped` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const doExport = async (kind, grouped) => {
    try {
      const params = new URLSearchParams();
      if (buyer.trim()) params.set("buyer", buyer.trim());
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (q.trim()) params.set("q", q.trim());
      if (grouped) params.set("grouped", "true");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/price-list/export.${kind}?${params.toString()}`;
      // Authenticated fetch → blob → force download (avoids opening a bare URL that would
      // 401 because the axios interceptor's Authorization header isn't attached to <a href>).
      const token = localStorage.getItem("token");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const fname = m ? m[1] : `price_list.${kind}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success(`Exported ${fname}`);
    } catch (e) {
      toast.error(`Export failed: ${e.message || e}`);
    }
  };

  // KPI counts derived from the current filtered list (buyer/q etc still applied server-side)
  const kpi = useMemo(() => {
    const c = { approved: 0, pending: 0, rejected: 0, no_price: 0 };
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  const openSubmit = (row) => {
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    setSubmitRow(row);
    setForm({
      currency: row.currency || (currencies[0] || "USD"),
      price: row.price ? String(row.price) : "",
      price_terms: row.price_terms || "",
      effective_date: iso,
    });
    setSubmitOpen(true);
  };

  const submitPrice = async () => {
    if (!submitRow) return;
    const n = Number(form.price);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Enter a valid positive price"); return; }
    if (!form.price_terms) { toast.error("Price Terms is required"); return; }
    if (!form.effective_date) { toast.error("Effective date is required"); return; }
    try {
      await api.post("/price-list/submit", {
        our_style: submitRow.our_style,
        currency: form.currency,
        price: n,
        price_terms: form.price_terms,
        effective_date: form.effective_date,
      });
      toast.success(`Submitted ${form.currency} ${n.toFixed(2)} (${form.price_terms}) for ${submitRow.our_style} — awaiting approval`);
      setSubmitOpen(false);
      setSubmitRow(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    }
  };

  const approve = async (entryId) => {
    try {
      await api.post(`/price-list/${entryId}/approve`);
      toast.success("Price approved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  const openReject = (entry) => {
    setRejectEntry(entry);
    setRejectReason("");
    setRejectOpen(true);
  };
  const submitReject = async () => {
    if (!rejectEntry) return;
    if (!rejectReason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/price-list/${rejectEntry.id}/reject`, { reason: rejectReason.trim() });
      toast.success("Marked Un-Approved");
      setRejectOpen(false);
      setRejectEntry(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  const withdraw = async (entryId) => {
    if (!window.confirm("Withdraw this pending submission?")) return;
    try {
      await api.delete(`/price-list/${entryId}`);
      toast.success("Withdrawn");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Withdraw failed"); }
  };

  const openHistory = async (row) => {
    try {
      const res = await api.get(`/price-list/history/${encodeURI(row.our_style)}`);
      setHistoryStyle(res.data.style);
      setHistoryEntries(res.data.entries || []);
      setHistoryOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load history"); }
  };

  return (
    <div className="space-y-4" data-testid="price-list-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500">Administration</div>
          <h1 className="font-heading font-bold text-3xl tracking-tight text-slate-900">Price List</h1>
          <div className="text-[12.5px] text-slate-500 mt-0.5">Master-linked price catalog. Merchandiser submits, Admin approves. Drill-down keeps full historical timeline per style.</div>
        </div>
        {canExport && (
          <div className="flex items-center gap-2">
            {canImport && (
              <Button variant="outline" className="border-slate-300 gap-1" onClick={() => { setImportFile(null); setImportResult(null); setImportOpen(true); }} data-testid="price-import-btn">
                <Upload className="h-4 w-4" />Historical Import
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-slate-300 gap-1" data-testid="price-export-menu">
                  <FileDown className="h-4 w-4" />Export
                  <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500">Excel (.xlsx)</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => doExport("xlsx", false)} data-testid="price-export-xlsx-consolidated">Consolidated (single sheet)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport("xlsx", true)} data-testid="price-export-xlsx-buyerwise">Buyer-wise (sheet per buyer)</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500">PDF</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => doExport("pdf", false)} data-testid="price-export-pdf-consolidated">Consolidated (single section)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => doExport("pdf", true)} data-testid="price-export-pdf-buyerwise">Buyer-wise (page per buyer)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* KPI tiles — small strip showing distribution */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="price-kpi-strip">
        {[
          { key: "approved",  label: "Approved",         value: kpi.approved,  Icon: Check,    tone: "bg-emerald-50 border-emerald-200 text-emerald-800", iconTone: "text-emerald-600" },
          { key: "pending",   label: "Pending Approval", value: kpi.pending,   Icon: History,  tone: "bg-amber-50 border-amber-200 text-amber-900",       iconTone: "text-amber-600" },
          { key: "rejected",  label: "Un-Approved",      value: kpi.rejected,  Icon: X,        tone: "bg-rose-50 border-rose-200 text-rose-800",          iconTone: "text-rose-600" },
          { key: "no_price",  label: "No Price Yet",     value: kpi.no_price,  Icon: IndianRupee, tone: "bg-slate-50 border-slate-200 text-slate-700",    iconTone: "text-slate-500" },
        ].map((c) => (
          <Card key={c.key} className={`p-4 rounded-md border ${c.tone}`} data-testid={`price-kpi-${c.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[10.5px] font-mono uppercase tracking-widest opacity-70">{c.label}</div>
                <div className="mt-1 font-heading font-bold text-2xl tracking-tight">{c.value}</div>
              </div>
              <c.Icon className={`h-5 w-5 shrink-0 ${c.iconTone}`} />
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="p-3 border-slate-200 rounded-md bg-white">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="Search Our Style / Buyer Style / Description…"
              className="pl-7"
              data-testid="price-search"
            />
          </div>
          <Input value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="Filter by buyer" data-testid="price-buyer-filter" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="price-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="pending">Pending Approval</SelectItem>
              <SelectItem value="rejected">Un-Approved</SelectItem>
              <SelectItem value="no_price">No price yet</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={load} variant="outline" className="border-slate-300" data-testid="price-refresh">Refresh</Button>
        </div>
      </Card>

      {/* Bulk-approve toolbar — shows when any pending row is selectable & user is approver */}
      {canApprove && (() => {
        const pendingRows = rows.filter((r) => r.status === "pending" && r.pending);
        const selectedCount = pendingRows.filter((r) => selectedPendingIds.has(r.pending.id)).length;
        if (pendingRows.length === 0) return null;
        const allSelected = selectedCount > 0 && selectedCount === pendingRows.length;
        const toggleAll = () => {
          setSelectedPendingIds((prev) => {
            const next = new Set(prev);
            if (allSelected) pendingRows.forEach((r) => next.delete(r.pending.id));
            else pendingRows.forEach((r) => next.add(r.pending.id));
            return next;
          });
        };
        return (
          <Card className="p-2.5 border-emerald-200 bg-emerald-50/60 flex flex-wrap items-center gap-3" data-testid="price-bulk-approve-toolbar">
            <label className="flex items-center gap-2 text-[12.5px] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = selectedCount > 0 && !allSelected; }}
                onChange={toggleAll}
                className="h-4 w-4 accent-emerald-600"
                data-testid="price-bulk-approve-select-all"
              />
              <span className="font-semibold text-slate-700">
                {selectedCount > 0 ? `${selectedCount} of ${pendingRows.length} selected` : `Select all ${pendingRows.length} pending`}
              </span>
            </label>
            <div className="ml-auto flex items-center gap-2">
              {selectedCount > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setSelectedPendingIds(new Set())} data-testid="price-bulk-approve-clear">Clear</Button>
              )}
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={selectedCount === 0}
                onClick={bulkApprove}
                data-testid="price-bulk-approve-btn"
              >
                <Check className="h-3.5 w-3.5 mr-1" />Approve {selectedCount || ""} selected
              </Button>
            </div>
          </Card>
        );
      })()}

      {/* Main table */}
      <Card className="p-0 border-slate-200 rounded-md bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1480px] table-fixed text-[13px]" data-testid="price-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider font-mono text-slate-500">
              <tr>
                {canApprove && (<th className="w-8 px-2 py-2"></th>)}
                <th className="text-left px-3 py-2 w-[110px]">Our Style</th>
                <th className="text-left px-3 py-2 w-[130px]">Buyer Style</th>
                <th className="text-left px-3 py-2 w-[100px]">Product Group</th>
                {/* No width here — Description is the flex column that absorbs slack */}
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-left px-3 py-2 w-[150px]">Buyer</th>
                {canSeePrices && (<th className="text-right px-3 py-2 w-[90px]">Price</th>)}
                {canSeePrices && (<th className="text-left px-3 py-2 w-[80px]">Price Terms</th>)}
                {canSeePrices && (<th className="text-left px-3 py-2 w-[95px]">Effective</th>)}
                <th className="text-left px-3 py-2 w-[115px]">Status</th>
                <th className="text-right px-3 py-2 whitespace-nowrap w-[185px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const colCount = (canApprove ? 1 : 0) + 6 + (canSeePrices ? 3 : 0) + 1;
                return (<>
                  {loading && (<tr><td colSpan={colCount} className="text-center py-8 text-slate-400">Loading…</td></tr>)}
                  {!loading && rows.length === 0 && (<tr><td colSpan={colCount} className="text-center py-8 text-slate-400">No styles match — adjust filters or seed the styles master.</td></tr>)}
                </>);
              })()}
              {!loading && rows.map((r) => {
                const badge = STATUS_BADGE[r.status] || STATUS_BADGE.no_price;
                const isPending = r.status === "pending" && r.pending;
                const isMine = r.pending && r.pending.submitted_by === user?.email;
                const isChecked = isPending && selectedPendingIds.has(r.pending.id);
                return (
                  <tr key={r.our_style} className={`border-b border-slate-100 hover:bg-slate-50/60 ${isChecked ? "bg-emerald-50/40" : ""}`} data-testid={`price-row-${r.our_style}`}>
                    {canApprove && (
                      <td className="px-2 py-2 text-center">
                        {isPending && (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedPendingIds((prev) => {
                                const next = new Set(prev);
                                if (checked) next.add(r.pending.id); else next.delete(r.pending.id);
                                return next;
                              });
                            }}
                            className="h-4 w-4 accent-emerald-600"
                            data-testid={`price-check-${r.our_style}`}
                            aria-label={`Select ${r.buyer_style} for bulk approve`}
                          />
                        )}
                      </td>
                    )}                    <td className="px-3 py-2 font-mono text-[12.5px] break-words">{r.our_style || "—"}</td>
                    <td className="px-3 py-2 break-words">{r.buyer_style || "—"}</td>
                    <td className="px-3 py-2 break-words">{r.product_group || "—"}</td>
                    <td className="px-3 py-2 whitespace-normal break-words text-[12.5px] leading-snug line-clamp-3" title={r.description}>{r.description || "—"}</td>
                    <td className="px-3 py-2 break-words">{r.buyer || "—"}</td>
                    {canSeePrices && (<td className="px-3 py-2 text-right font-mono">{fmtMoney(r.currency, r.price)}</td>)}
                    {canSeePrices && (<td className="px-3 py-2 font-mono text-[12px]">{r.price_terms || "—"}</td>)}
                    {canSeePrices && (<td className="px-3 py-2 text-[12px]">{fmtDate(r.effective_date)}</td>)}
                    <td className="px-3 py-2">
                      <Badge className={`${badge.tone} border font-semibold`} data-testid={`price-badge-${r.our_style}`}>{badge.label}</Badge>
                      {r.status === "rejected" && r.last_rejected?.reject_reason && (
                        <div className="text-[10.5px] text-rose-600 mt-0.5 max-w-[200px] truncate" title={r.last_rejected.reject_reason}>Reason: {r.last_rejected.reject_reason}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1 items-center">
                        {canSubmit && (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11.5px]" onClick={() => openSubmit(r)} data-testid={`price-submit-btn-${r.our_style}`}>
                            <Plus className="h-3 w-3 mr-0.5" />
                            {r.status === "approved" || r.status === "rejected" ? "Update" : "Submit"}
                          </Button>
                        )}
                        {isPending && canApprove && (
                          <>
                            <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[11.5px]" onClick={() => approve(r.pending.id)} data-testid={`price-approve-btn-${r.our_style}`}>
                              <Check className="h-3 w-3 mr-0.5" />Approve
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 px-2 border-rose-300 text-rose-700 hover:bg-rose-50 text-[11.5px]" onClick={() => openReject(r.pending)} data-testid={`price-reject-btn-${r.our_style}`}>
                              <X className="h-3 w-3 mr-0.5" />Reject
                            </Button>
                          </>
                        )}
                        {isPending && !canApprove && isMine && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-600 text-[11.5px]" onClick={() => withdraw(r.pending.id)} data-testid={`price-withdraw-btn-${r.our_style}`}>
                            <Undo2 className="h-3 w-3 mr-0.5" />Withdraw
                          </Button>
                        )}
                        {r.history_count > 0 && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-600 text-[11.5px]" onClick={() => openHistory(r)} data-testid={`price-history-btn-${r.our_style}`}>
                            <History className="h-3 w-3 mr-0.5" />
                            {r.history_count}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Submit / Update dialog */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent data-testid="price-submit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><IndianRupee className="h-5 w-5 text-brand" />Submit price for approval</DialogTitle>
          </DialogHeader>
          {submitRow && (
            <div className="text-[13px] text-slate-600">
              <div className="font-semibold">{submitRow.buyer} · {submitRow.buyer_style}</div>
              <div className="text-[12px] text-slate-500">Our Style: <span className="font-mono">{submitRow.our_style}</span> · {submitRow.product_group || "—"}</div>
            </div>
          )}
          <div className="space-y-3 mt-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1 block">Currency <span className="text-rose-600">*</span></label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="price-submit-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1 block">Price <span className="text-rose-600">*</span></label>
                <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="e.g. 12.50" data-testid="price-submit-value" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1 block">Price Terms <span className="text-rose-600">*</span></label>
                <Select value={form.price_terms} onValueChange={(v) => setForm({ ...form, price_terms: v })}>
                  <SelectTrigger data-testid="price-submit-terms"><SelectValue placeholder="Select terms" /></SelectTrigger>
                  <SelectContent>
                    {priceTerms.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1 block">Effective from <span className="text-rose-600">*</span></label>
                <Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} data-testid="price-submit-date" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitOpen(false)}>Cancel</Button>
            <Button className="bg-brand text-white hover:bg-brand-dark" onClick={submitPrice} data-testid="price-submit-confirm">Submit for approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="price-reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><X className="h-5 w-5 text-rose-600" />Mark as Un-Approved</DialogTitle>
          </DialogHeader>
          <div className="text-[13px] text-slate-600">Reason will be shown on the row and captured in the audit trail.</div>
          <Input
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Below cost floor · needs re-quote"
            data-testid="price-reject-reason"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={submitReject} data-testid="price-reject-confirm">Mark Un-Approved</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History drill-down */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl" data-testid="price-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-brand" />Price history</DialogTitle>
          </DialogHeader>
          {historyStyle && (
            <div className="text-[13px] text-slate-600 mb-2">
              <div className="font-semibold">{historyStyle.buyer} · {historyStyle.buyer_style}</div>
              <div className="text-[12px] text-slate-500">Our Style: <span className="font-mono">{historyStyle.our_style}</span> · {historyStyle.name || "—"}</div>
            </div>
          )}
          <div className="max-h-[60vh] overflow-y-auto">
            {historyEntries.length === 0 && (<div className="text-center py-6 text-slate-400 text-sm">No history yet.</div>)}
            {historyEntries.length > 0 && (
              <table className="min-w-full text-[12.5px]">
                <thead className="bg-slate-50 border-b text-[11px] uppercase tracking-wider font-mono text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-1.5">Submitted</th>
                    <th className="text-left px-3 py-1.5">By</th>
                    {canSeePrices && (<th className="text-right px-3 py-1.5">Price</th>)}
                    {canSeePrices && (<th className="text-left px-3 py-1.5">Terms</th>)}
                    {canSeePrices && (<th className="text-left px-3 py-1.5">Effective</th>)}
                    <th className="text-left px-3 py-1.5">Status</th>
                    <th className="text-left px-3 py-1.5">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {historyEntries.map((e) => {
                    const isCurrent = e.status === "approved" && !e.superseded_at;
                    const badge = STATUS_BADGE[e.status] || STATUS_BADGE.no_price;
                    return (
                      <tr key={e.id} className={`border-b border-slate-100 ${isCurrent ? "bg-emerald-50/40" : ""}`}>
                        <td className="px-3 py-1.5 font-mono text-[11.5px]">{fmtDate(e.submitted_at)}</td>
                        <td className="px-3 py-1.5">{e.submitted_by_name || e.submitted_by}</td>
                        {canSeePrices && (<td className="px-3 py-1.5 text-right font-mono">{fmtMoney(e.currency, e.price)}</td>)}
                        {canSeePrices && (<td className="px-3 py-1.5 font-mono text-[11.5px]">{e.price_terms || "—"}</td>)}
                        {canSeePrices && (<td className="px-3 py-1.5 text-[12px]">{fmtDate(e.effective_date)}</td>)}
                        <td className="px-3 py-1.5"><Badge className={`${badge.tone} border font-semibold`}>{isCurrent ? "Current" : badge.label}</Badge></td>
                        <td className="px-3 py-1.5 text-[11.5px] text-slate-500">
                          {e.status === "approved" && e.approved_by_name && (<>Approved by <b>{e.approved_by_name}</b> on {fmtDate(e.approved_at)}</>)}
                          {e.status === "rejected" && (<>By {e.rejected_by_name || e.rejected_by} — <i>{e.reject_reason}</i></>)}
                          {e.superseded_at && (<div className="text-[10.5px] text-slate-400 mt-0.5">Superseded on {fmtDate(e.superseded_at)}</div>)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Historical import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="price-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-brand" />Historical Price Import</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[13px] text-slate-600">
            <div>Upload a filled Excel template to seed baseline prices. Each valid row is imported as an <b>approved</b> baseline and supersedes any prior approved price for the same style. Invalid rows are skipped and listed after import.</div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={downloadImportTemplate} data-testid="price-import-template-btn">
                <FileDown className="h-3.5 w-3.5 mr-1" />Download template
              </Button>
              <span className="text-[11.5px] text-slate-400">Required columns: Our Style · Currency · Price · Price Terms · Effective Date</span>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1 block">Filled .xlsx</label>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                className="text-[12.5px]"
                data-testid="price-import-file"
              />
              {importFile && <div className="text-[11.5px] text-slate-500 mt-1">{importFile.name} · {(importFile.size / 1024).toFixed(1)} KB</div>}
            </div>
            {importResult && (
              <Card className="p-3 border-emerald-200 bg-emerald-50/60 text-[12.5px]" data-testid="price-import-result">
                <div className="font-semibold text-emerald-800">
                  Imported {importResult.imported} row(s) · superseded {importResult.superseded} prior approved
                  {importResult.errors?.length ? ` · ${importResult.errors.length} skipped` : ""}
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto text-[11.5px] text-rose-700 space-y-0.5">
                    {importResult.errors.slice(0, 50).map((er, i) => (
                      <div key={i}><b>Row {er.row}:</b> {er.reason}</div>
                    ))}
                    {importResult.errors.length > 50 && <div className="italic">…and {importResult.errors.length - 50} more</div>}
                  </div>
                )}
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
            <Button className="bg-brand text-white hover:bg-brand-dark" onClick={doHistoricalImport} disabled={!importFile || importing} data-testid="price-import-submit">
              {importing ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
